import { Injectable, TemplateRef, inject } from '@angular/core';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, Store } from '@ngrx/store';
import {
    List,
    ListMember,
    ListType,
    LookupFieldResult,
    Party,
    PartyRole,
    Person,
    Query,
    SystemEntity,
} from '@wdx/clmi/clmi-swagger-gen';
import { QuickLink } from '@wdx/shared/utils';
import {
    Observable,
    Subject,
    filter,
    map,
    take,
    tap,
    withLatestFrom,
} from 'rxjs';
import { ENTITY_ICON_MAP } from '../../../../constants/entity-icons.constants';
import { InfoCardData } from '../../../../models/info-card-data.model';
import { ModalManagementService } from '@wdx/clmi/ui';
import * as listsActions from '../../../../state/lists/lists.actions';
import * as listsSelectors from '../../../../state/lists/lists.selectors';
import {
    operationsActions,
    operationsSelectors,
} from '@wdx/clmi/api-services/state';

@Injectable({
    providedIn: 'root',
})
export class ListFacade {
    listId = '';
    standardList = { code: 'standard' };

    marketingLists$ = new Subject<List[]>();
    marketingListsIsLoading$: Observable<boolean>;
    marketingListsHasError$: Observable<boolean>;

    list$: Observable<List>;
    listIsLoading$: Observable<boolean>;
    listHasError$: Observable<boolean>;

    members$: Observable<ListMember[]>;
    membersIsLoading$: Observable<boolean>;
    membersHasError$: Observable<boolean>;

    parties$: Observable<Party[]>;
    partiesIsLoading$: Observable<boolean>;
    partiesHasError$: Observable<boolean>;

    partyRoles$: Observable<PartyRole[]>;
    partyRolesIsLoading$: Observable<boolean>;
    partyRolesHasError$: Observable<boolean>;

    people$: Observable<Person[]>;
    peopleIsLoading$: Observable<boolean>;
    peopleHasError$: Observable<boolean>;

    private store$ = inject(Store);
    private actionsSubject$ = inject(ActionsSubject);
    private modalManagementService = inject(ModalManagementService);

    loadMarketingLists(): void {
        this.store$.dispatch(listsActions.getLists());
    }

    setMarketingLists(): void {
        this.marketingListsIsLoading$ = this.store$.select(
            listsSelectors.getIsLoadingLists,
        );
        this.marketingListsHasError$ = this.store$.select(
            listsSelectors.getHasLoadingListsError,
        );

        this.store$
            .select(listsSelectors.getListsByType('standard'))
            .subscribe((listData) => {
                this.listsAsInfoCardData(listData);
            });
    }

    loadList(listId: string): void {
        this.store$.dispatch(listsActions.getListForId({ listId }));
    }

    setList(listId: string): void {
        this.list$ = this.store$.select(listsSelectors.getListForId(listId));
        this.listIsLoading$ = this.store$.select(
            listsSelectors.getIsLoadingListForId,
            { listId },
        );
        this.listHasError$ = this.store$.select(
            listsSelectors.getHasLoadingListForIdError,
            { listId },
        );
    }

    getList(listId): Observable<List> {
        this.listId = listId;
        this.setList(listId);
        return this.list$.pipe(
            withLatestFrom(this.listIsLoading$, this.listHasError$),
            tap(([list, listIsLoading, listHasError]) => {
                if (!list && !listIsLoading && !listHasError) {
                    this.loadList(listId);
                }
            }),
            map(([list]) => list),
        );
    }

    loadParties(listId: string): void {
        this.store$.dispatch(listsActions.getPartiesForId({ listId }));
    }

    loadPartyRoles(listId: string): void {
        this.store$.dispatch(listsActions.getPartyRolesForId({ listId }));
    }

    loadMembers(listId: string): void {
        this.store$.dispatch(listsActions.getMembersForId({ listId }));
    }

    setParties(listId: string): void {
        this.parties$ = this.store$.select(
            listsSelectors.getPartiesForId(listId),
        );
        this.partiesIsLoading$ = this.store$.select(
            listsSelectors.getIsLoadingPartiesForId(listId),
        );
        this.partiesHasError$ = this.store$.select(
            listsSelectors.getHasLoadingPartiesForIdError(listId),
        );
    }

    setPartyRoles(listId: string): void {
        this.partyRoles$ = this.store$.select(
            listsSelectors.getPartyRolesForId(listId),
        );
        this.partyRolesIsLoading$ = this.store$.select(
            listsSelectors.getIsLoadingPartyRolesForId(listId),
        );
        this.partyRolesHasError$ = this.store$.select(
            listsSelectors.getHasLoadingPartyRolesForIdError(listId),
        );
    }

    setMembers(listId: string): void {
        this.members$ = this.store$.select(
            listsSelectors.getMembersForId(listId),
        );
        this.membersIsLoading$ = this.store$.select(
            listsSelectors.getIsLoadingMembersForId(listId),
        );
        this.membersHasError$ = this.store$.select(
            listsSelectors.getHasLoadingMembersForIdError(listId),
        );
    }

    onAddPartiesToList(args: { parties: Party[]; list: List }): void {
        if (!args.parties && !args.list) {
            return;
        }

        this.store$.dispatch(
            listsActions.addPartiesToList({
                list: args.list,
                parties: args.parties,
            }),
        );
    }

    onAddLookupResultsToList(args: {
        lookupResults: LookupFieldResult[];
        list: List;
    }): void {
        if (!args.lookupResults && !args.list) {
            return;
        }

        this.store$.dispatch(
            listsActions.addLookupResultsToList({
                list: args.list,
                lookupResults: args.lookupResults,
            }),
        );
    }

    onListActionsSuccessful(): Observable<any> {
        return this.actionsSubject$.pipe(
            ofType(
                listsActions.addLookupResultsToListSuccess,
                listsActions.deleteEntityFromListSuccess,
            ),
        );
    }

    onRemoveMemberFromList(
        entityId: string,
        entityType: SystemEntity,
        listId?: string,
    ): void {
        if (!entityId || !entityType) {
            return;
        }

        this.store$.dispatch(
            listsActions.deleteEntityFromList({
                listId: listId || this.listId,
                entityId,
                entityType,
            }),
        );
    }

    deleteEntityFromList(
        listId: string,
        entityType: SystemEntity,
        entityId: string,
    ): void {
        this.store$.dispatch(
            listsActions.deleteEntityFromList({
                listId,
                entityType,
                entityId,
            }),
        );
    }

    listsAsInfoCardData(lists: List[]): void {
        const INFO_CARD_DATA: InfoCardData[] = [];

        lists?.map((list) => {
            INFO_CARD_DATA.push({
                id: list.id,
                primaryInfo: list.name,
                secondaryInfo: [{ label: list.description }],
                tertiaryInfo: [
                    { label: `${list.numberOfMembers || 0} members` },
                ],
                icon: ENTITY_ICON_MAP[SystemEntity.List],
                data: list,
            });
        });

        this.marketingLists$.next(INFO_CARD_DATA);
    }

    addToListWithQuery(list: List, entityName: string, query: Query): void {
        this.actionsSubject$
            .pipe(ofType(listsActions.addListResultWithQuerySuccess), take(1))
            .subscribe({
                next: () => {
                    this.modalManagementService.closeActiveModal();
                },
            });

        this.store$.dispatch(
            listsActions.addListResultWithQuery({
                listId: list.id,
                entityName: entityName,
                query: query,
            }),
        );
    }

    loadStandardLists(): void {
        this.store$.dispatch(
            listsActions.getListsByType({ listType: this.standardList }),
        );
    }

    getOperationLinksIsLoading$(listId: string): Observable<boolean> {
        return this.store$.select(
            operationsSelectors.operationsForIdIsLoadingSingle,
            {
                entityId: listId,
                entityType: SystemEntity.List,
            },
        );
    }

    getOperationLinksHasError$(listId: string): Observable<boolean> {
        return this.store$.select(
            operationsSelectors.operationsForIdHasLoadingSingleError,
            {
                entityId: listId,
                entityType: SystemEntity.List,
            },
        );
    }

    getOperationLinks$(listId: string): Observable<QuickLink[]> {
        return this.store$
            .select(
                operationsSelectors.getLinksForId({
                    entityId: listId,
                    entityType: SystemEntity.List,
                }),
            )
            .pipe(
                tap((links) => {
                    if (!links) {
                        this.store$.dispatch(
                            operationsActions.getOperationsForId({
                                entityType: SystemEntity.List,
                                entityId: listId,
                            }),
                        );
                    }
                }),
                filter(Boolean),
            );
    }
}
